import { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Page from '~/components/Page';
import Button from '~/components/buttons/NewButton';
import FAQ from '~/components/data-display/FAQ/FAQ';
import { VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { useAnalytics } from '~/hooks/useAnalytics';
import IconArrowRight from '~/icons/icon-arrow-right.svg?react';
import heroIllustration from '~/images/ai-chat-selling/hero-illustration.png';
import { useModal } from '~/modals/Modal';
import AiChatTrialRequestModal from '~/modals/chat/AiChatTrialRequestModal';
import { AnalyticsEvents } from '~/services/analytics';
import styles from './AiChatSellingPage.module.scss';
import AiChatSellingVideo from './components/AiChatSellingVideo';

type TrialRequestSection = 'cover' | 'banner' | 'footer';

const AiChatSellingPage: React.FC = () => {
  const { t } = useTranslation('chat', { keyPrefix: 'selling' });
  const { track } = useAnalytics();
  const { showModal } = useModal();
  const howItWorksRef = useRef<HTMLDivElement>(null);

  const handleStartTrial = (section: TrialRequestSection) => {
    track(AnalyticsEvents.PERLA_REQUEST_STARTED, { section });
    showModal({ component: AiChatTrialRequestModal, props: {} });
  };

  const handleDiscoverMore = () => {
    track(AnalyticsEvents.PERLA_DISCOVER_MORE_CLICKED);
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const questionCards = [
    { question: t('questions.cashflow.question'), answer: t('questions.cashflow.answer') },
    { question: t('questions.savings.question'), answer: t('questions.savings.answer') },
    { question: t('questions.late_payers.question'), answer: t('questions.late_payers.answer') },
    { question: t('questions.overdue_invoices.question'), answer: t('questions.overdue_invoices.answer') },
  ];

  const faqItems = [
    { question: t('faq.what_is_perla.question'), answer: t('faq.what_is_perla.answer') },
    { question: t('faq.trial.question'), answer: t('faq.trial.answer') },
    { question: t('faq.pricing.question'), answer: t('faq.pricing.answer') },
    { question: t('faq.what_to_ask.question'), answer: t('faq.what_to_ask.answer') },
    { question: t('faq.vs_other_ai.question'), answer: t('faq.vs_other_ai.answer') },
    { question: t('faq.data_security.question'), answer: t('faq.data_security.answer') },
  ];

  return (
    <Page className={styles.page} title={t('hero.title')}>
      <VStack gap={5} className={styles.content}>
        <VStack gap={3} className={styles.section}>
          <div className={styles.hero}>
            <VStack gap={2} className={styles.heroInfo}>
              <Text variant="title3" weight="semibold" className={styles.heroTitle}>
                {t('hero.title')}
              </Text>
              <Text variant="body1" weight="regular" element="p" className={styles.heroDescription}>
                <Trans t={t} i18nKey="hero.description" components={{ strong: <strong />, br: <br /> }} />
              </Text>
              <div className={styles.heroActions}>
                <Button Icon={IconArrowRight} onClick={() => handleStartTrial('cover')}>
                  {t('hero.start_trial')}
                </Button>
                <Button variant="outlined" className={styles.heroSecondaryButton} onClick={handleDiscoverMore}>
                  {t('hero.discover_more')}
                </Button>
              </div>
            </VStack>
            <img src={heroIllustration} alt="" className={styles.heroIllustration} />
          </div>
        </VStack>

        <VStack gap={3} className={styles.section} ref={howItWorksRef}>
          <Text variant="title4" weight="semibold">
            {t('how_it_works.title')}
          </Text>
          <div className={styles.videoWrapper}>
            <AiChatSellingVideo title={t('how_it_works.title')} />
          </div>
        </VStack>

        <VStack gap={3} className={styles.section}>
          <Text variant="title4" weight="semibold">
            {t('questions.title')}
          </Text>
          <div className={styles.questionGrid}>
            {questionCards.map(({ question, answer }) => (
              <VStack gap={1.5} key={question} className={styles.questionCard}>
                <Text variant="body1" weight="semibold" tabularNums>
                  {question}
                </Text>
                <div className={styles.answerChip}>
                  <Text variant="body2" weight="regular">
                    {answer}
                  </Text>
                </div>
              </VStack>
            ))}
          </div>
          <VStack gap={1.5} align="center" className={styles.ctaCard}>
            <Text variant="subtitle1" align="center" className={styles.ctaCardText}>
              {t('questions.cta_title')}
            </Text>
            <Text variant="body1" weight="regular" align="center" className={styles.ctaCardText}>
              {t('questions.cta_description')}
            </Text>
            <Button Icon={IconArrowRight} onClick={() => handleStartTrial('banner')}>
              {t('hero.start_trial')}
            </Button>
          </VStack>
        </VStack>

        <div className={styles.section}>
          <FAQ title={t('faq.title')} items={faqItems} titleProps={{ variant: 'title4', weight: 'semibold' }} />
        </div>

        <div className={styles.footerActions}>
          <Button Icon={IconArrowRight} onClick={() => handleStartTrial('footer')}>
            {t('start_trial')}
          </Button>
        </div>
      </VStack>
    </Page>
  );
};

export default AiChatSellingPage;
